import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Download } from 'lucide-react';
import { Slider } from '../ui/slider';

const THEMES = [
  { id: 'cream', bg: '#fdf6ea', fg: '#3a2a15', label: 'Cream' },
  { id: 'white', bg: '#ffffff', fg: '#1f1a14', label: 'White' },
  { id: 'ink', bg: '#1f1a14', fg: '#f6e7cf', label: 'Ink' },
  { id: 'rose', bg: '#fbe4e6', fg: '#6b1f2c', label: 'Rose' },
  { id: 'sage', bg: '#e5efe4', fg: '#233a26', label: 'Sage' },
];

// Keep explicit blank lines and split long words without separating combining marks.
function wrapLines(ctx, text, width) {
  const segmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;
  const split = (word) => segmenter ? Array.from(segmenter.segment(word), ({ segment }) => segment) : Array.from(word);
  const lines = [];
  for (const paragraph of text.replace(/\r\n?/g, '\n').split('\n')) {
    let line = '';
    for (const token of paragraph.match(/\S+|\s+/gu) || []) {
      if (line && ctx.measureText(line + token).width > width) { lines.push(line); line = ''; }
      for (const character of split(token)) {
        if (line && ctx.measureText(line + character).width > width) { lines.push(line); line = ''; }
        line += character;
      }
    }
    lines.push(line);
  }
  return lines;
}

export default function ImageModal({ open, onOpenChange, text }) {
  const ref = useRef(null);
  const [theme, setTheme] = useState(THEMES[0]);
  const [fs, setFs] = useState(56);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => {
      const c = ref.current;
      if (!c) return;
      const ctx = c.getContext('2d');
      c.width = 1200;
      const font = (size) => `${size}px 'Segoe UI Symbol', 'Noto Sans Math', 'Cambria Math', serif`;
      let size = fs;
      let lines;
      let lh;
      let height;
      do {
        ctx.font = font(size);
        lines = wrapLines(ctx, text, c.width - 160);
        lh = Math.max(size * 1.5, ...lines.map((line) => {
          const metrics = ctx.measureText(line);
          return (metrics.actualBoundingBoxAscent || size) + (metrics.actualBoundingBoxDescent || size * 0.3) + size * 0.2;
        }));
        height = Math.ceil(lines.length * lh + 160);
        if (height <= 16000) break;
        size *= 0.8;
      } while (size > 0.5);
      c.height = Math.max(630, height);
      ctx.fillStyle = theme.bg; ctx.fillRect(0, 0, c.width, c.height);
      ctx.strokeStyle = theme.fg + '33'; ctx.lineWidth = 2; ctx.strokeRect(30, 30, c.width - 60, c.height - 60);
      ctx.fillStyle = theme.fg; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.font = font(size);
      const start = c.height / 2 - ((lines.length - 1) * lh) / 2;
      lines.forEach((l, i) => ctx.fillText(l, c.width / 2, start + i * lh, c.width - 160));
    });
    return () => cancelAnimationFrame(id);
  }, [open, text, theme, fs]);

  const download = () => {
    const a = document.createElement('a');
    a.href = ref.current.toDataURL('image/png');
    a.download = 'cursive-text.png';
    a.click();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[640px] max-h-[90vh] overflow-y-auto font-outfit" data-testid="image-modal">
        <DialogHeader><DialogTitle className="font-garamond text-2xl">Save as Image</DialogTitle><DialogDescription>PNG preview. The image height adapts to fit your text.</DialogDescription></DialogHeader>
        <canvas ref={ref} className="w-full rounded-md border border-[var(--line)]" data-testid="image-canvas" />
        <div className="flex flex-wrap items-center gap-2">
          {THEMES.map((th) => (
            <button key={th.id} onClick={() => setTheme(th)} title={th.label}
              className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${theme.id === th.id ? 'border-[var(--gold)]' : 'border-[var(--line)]'}`}
              style={{ background: th.bg }} data-testid={`theme-${th.id}`} />
          ))}
          <div className="flex items-center gap-2 ml-auto">
            <span className="ctrl-label">Size</span>
            <Slider aria-label="Text size" value={[fs]} min={28} max={96} step={2} onValueChange={(v) => setFs(v[0])} className="w-28" />
          </div>
        </div>
        <button className="btn-gold inline-flex items-center justify-center gap-2 py-2" onClick={download} data-testid="download-png">
          <Download className="w-4 h-4" /> Download PNG
        </button>
      </DialogContent>
    </Dialog>
  );
}
